import { min, max } from 'd3-array';

import IndicatorFlag from '../flags/IndicatorFlag';
import Indicator from '../Indicator';
import subpixel from '../util/canvasSubpixelOffset';
import translate from '../util/translate';

/** Previous Close indicator */
export default class PreviousCloseIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {object[]} inputs - Indicator inputs
	 * @property {string} description - Indicator description
	 * @property {null} text - Indicator text
	 * @property {boolean} showFlag - Whether to show a Y axis flag
	 * @property {string} labelAlignment - How to align label - 'ne' | 'nw' | 'sw' | 'se'
	 * @property {number} numDaysLabel - Number of days to label. -1 for all days, 0 for none
	 * @property {number} numDaysLine - Number of days to render line. -1 for all days, 0 for none
	 * @property {boolean} noClip - Whether to clip the indicator
	 */
	_getDefaultParams() {
		return {
			id: 'previousclose',
			name: 'Previous Close',
			inputs: [{ name: 'Version', value: 1 }],
			description:
				"Previous Close graphically depicts today's net change. A horizontal line is drawn at the vertical position of the previous day's close. When the chart style is set to mountain, the fill color is typically red below this line, or green above the line.",
			text: null,
			showFlag: false, // optional yaxis flag
			labelAlignment: 'nw', // ne, nw, sw, se
			numDaysLabel: 1, // <int> number of days to label (-1 = all days, 0 = none)
			numDaysLine: -1, // <int> number of days to render line (-1 = all days, 0 = none)
			noClip: true
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return data.previousclose.length
			? data.previousclose[data.previousclose.length - 1]
				.previousclose * 0.99 : 0;
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return data.previousclose.length
			? data.previousclose[data.previousclose.length - 1]
				.previousclose * 1.01 : 0;
	}

	/** @return {object} */
	_getMarkers() {
		return {};
	}

	/** @return {object} */
	_getDataMap() {
		return {};
	}

	/**
	 * Returns a formatted string for the indicator label
	 * @param {object} data - Indicator data
	 * @return {string} formatted label text
	 */
	_getLabelText(data) {
		const { yAxis } = this.panel;
		const { core } = this;
		const formatString = yAxis
			.getFormatString(yAxis.primaryScale)
			.replace(/s/, 'f'); // Use fixed point notation
		const valueFormat = yAxis.getFormatValue(data.previousclose, formatString);

		return typeof this.params.text === 'string'
			? this.params.text
			: translate(
				core.locale,
				'indicator.previousclose.label',
				[valueFormat]
			);
	}

	/**
	 * Returns an object with dimension specifications for this indicator
	 * @param {object} row - Current dimension object
	 * @param {number} index - Current dimension index
	 * @param {object[]} arr - Full dimensions array
	 * @return {object}
	 */
	_getDimensions(row, index, arr) {
		const { core, panel } = this;
		const ctx = panel.rootContext;
		const size = panel.size;
		const paddingLeft = size.padding.left;
		let x = paddingLeft;
		let dayLeft = 0;
		let dayRight = 0;
		const yClose = this.yScale(row.previousclose);
		let y = yClose;
		let labelWidth = 0;
		let labelHeight = 0;
		let labelText = '';
		let rightEdgeChart = paddingLeft + size.width;
		let leftEdgeChart = paddingLeft;
		const isNorth = /ne|nw/.test(this.params.labelAlignment);
		const isWest = /nw|sw/.test(this.params.labelAlignment);

		// edge adjustments
		rightEdgeChart = Math.min(
			rightEdgeChart,
			panel.xAxis.primaryScale(
				core.closestExchangeIndex(
					new Date(core.exchangeDates[core.exchangeDates.length - 1])
				)
			)
		);
		leftEdgeChart = Math.max(
			leftEdgeChart,
			panel.xAxis.primaryScale(
				core.closestExchangeIndex(new Date(core.exchangeDates[0]))
			)
		);

		if (!isNaN(y)) {
			ctx.font = panel.getFontStyle(
				this.params.style.label.font.weight,
				this.params.style.label.font.size,
				this.params.style.label.font.family
			);

			labelText = this._getLabelText(row);
			labelWidth = ctx.measureText(labelText).width;
			labelHeight = /(\d+)px/.test(ctx.font)
				? Number(RegExp.$1) * 1.2
				: 12; // rough height calc

			// initial x, y
			x = isWest ? paddingLeft + 5 : rightEdgeChart - labelWidth - 5;
			y += isNorth ? -labelHeight - 1 : 2;

			// adjust y for margin
			if (isNorth && this.params.style.margin.bottom) {
				y -= this.params.style.margin.bottom;
			}
			if (!isNorth && this.params.style.margin.top) {
				y += this.params.style.margin.top;
			}

			if (this.version === 2) {
				x =
					5 +
					panel.xAxis.primaryScale(
						core.closestExchangeIndex(new Date(row.date))
					);
			}

			const dateStart = new Date(row.date);
			let dateEnd;

			if (index < arr.length - 1) {
				dateEnd = new Date(arr[index + 1].date);
			} else {
				dateEnd = new Date(
					core.dataPrimary[core.dataPrimary.length - 1].date
				);
				dateEnd.setHours(0);
				dateEnd.setSeconds(0);
				dateEnd.setMilliseconds(0);

				if (core.state.isIntraday && core.state.typicalSessions) {
					dateEnd.setMinutes(core.state.typicalSessions.max);
				}
			}

			dayLeft = panel.xAxis.primaryScale(
				core.closestExchangeIndex(dateStart)
			);
			dayRight = Math.min(
				panel.xAxis.primaryScale(core.closestExchangeIndex(dateEnd)),
				panel.size.width
			);
		}

		return {
			x,
			y,
			dayLeft,
			dayRight,
			widthDay: dayRight - dayLeft,
			yClose,
			prevClose: row.previousclose,
			labelText,
			width: labelWidth,
			height: labelHeight,
			rightEdgeChart,
			leftEdgeChart
		};
	}

	/**
	 * Checks whether two rectangles on the canvas intersect
	 * @param {object} r1 - Rectangle 1 coordinates and size
	 * @param {object} r2 - Rectangle 2 coordinates and size
	 * @return {boolean}
	 */
	intersect(r1, r2) {
		if (
			r1.x < r2.x + r2.width &&
			r2.x < r1.x + r1.width &&
			r1.y < r2.y + r2.height
		) {
			return r2.y < r1.y + r1.height;
		}
		return false;

	}
	/**
	 * 
	 * @param {object} data
	 * @returns array of bounding boxes, one for each day
	 * @example: [
			{
				"x": 368.3333333333333,
				"y": 55.786838679133076,
				"dayLeft": 363.3333333333333,
				"dayRight": 545,
				"widthDay": 181.66666666666669,
				"yClose": 48.786838679133076,
				"labelText": "Previous Close 194.03",
				"prevClose": 194.03,
				"width": 100.615234375,
				"height": 12,
				"rightEdgeChart": 545,
				"leftEdgeChart": 0
			},
			...
		]
	 */
	_getV2BoundingBoxes(data) {
		const dimensionsAll = [];

		// bounding boxes
		data.previousclose.forEach((row, index, arr) => {
			dimensionsAll.push(this._getDimensions(row, index, arr));
		});

		return dimensionsAll;
	}

	/** @param {object} data Symbol data object */
	render(data) {
		const self = this;

		// lines
		if (this.version === 2) {
			const dimensionsAll = this._getV2BoundingBoxes(data);

			self._renderLineV2(dimensionsAll);

			dimensionsAll.forEach((row, index, arr) => {
				self._renderLabel(row, index, arr);
			});
		} else {
			const index = data.previousclose.length - 1;
			const dimensions = self._getDimensions(
				data.previousclose[index],
				index,
				data.previousclose
			);

			self._renderLine(dimensions);
			self._renderLabel(dimensions, 0, 1);
		}

		if (this.params.showFlag && !this.flags['previousclose']) {
			this.flags['previousclose'] = new IndicatorFlag({
				panel: this.panel,
				indicator: this,
				id: 'previousclose'
			});
		}
	}

	/**
	 * Renders a line
	 * @param {object} dimensions - Indicator dimensions
	 * @return {number}
	 */
	_renderLine(dimensions) {
		const { panel } = this;
		const ctx = panel.rootContext;

		ctx.textBaseline = 'top';
		ctx.lineWidth = this.params.style.width;
		ctx.strokeStyle = this.params.style.color;
		if (this.params.style.lineDash) {
			ctx.setLineDash(this.params.style.lineDash.split(/\s*,\s*/));
		}
		ctx.beginPath();
		subpixel.moveTo(ctx, dimensions.leftEdgeChart, dimensions.yClose);
		subpixel.lineTo(ctx, dimensions.rightEdgeChart, dimensions.yClose);
		ctx.stroke();
		ctx.setLineDash([]);
		return dimensions.rightEdgeChart - dimensions.leftEdgeChart;
	}

	/**
	 * Renders a line
	 * @param {object} dimensions - Indicator dimensions
	 * @return {number}
	 */
	_renderLineV2(dimensions) {
		const { panel } = this;
		const self = this;
		const ctx = panel.rootContext;
		ctx.textBaseline = 'top';
		ctx.lineWidth = this.params.style.width;
		ctx.strokeStyle = this.params.style.color;
		if (this.params.style.lineDash) {
			ctx.setLineDash(this.params.style.lineDash.split(/\s*,\s*/));
		}
		ctx.beginPath();

		dimensions.forEach((row, index, arr) => {
			if (self.params.numDaysLine === 0) return;

			if (
				self.params.numDaysLine > 0 &&
				index < arr.length - self.params.numDaysLine
			) {
				return;
			}

			if (row.dayRight < row.dayLeft) return;

			if (row.yClose < 0 || row.yClose >= panel.size.height) return;

			subpixel.moveTo(ctx, row.dayLeft, row.yClose);
			subpixel.lineTo(ctx, row.dayRight, row.yClose);
		});
		ctx.stroke();
		ctx.setLineDash([]);
	}

	/**
	 * Renders a label for the indicator
	 * @param {object} dimensions - Indicator dimensions
	 * @param {number} index - Current array index
	 * @param {object[]} arr - Array of data
	 */
	_renderLabel(dimensions, index, arr) {
		const { panel } = this;
		const ctx = panel.rootContext;

		if (this.params.numDaysLabel === 0) return;

		if (
			this.params.numDaysLabel > 0 &&
			index < arr.length - this.params.numDaysLabel
		) {
			return;
		}

		if (!isNaN(dimensions.y)) {
			if (
				this.version === 1 ||
				(dimensions.x + dimensions.width < panel.size.width &&
					dimensions.width < dimensions.widthDay)
			) {
				// label
				this.panel.glowText(
					ctx,
					dimensions.labelText,
					dimensions.x,
					dimensions.y,
					this.params.style.label.color,
					this.params.style.label.glow.color
				);
			}

			// debug bounding box
			//ctx.strokeStyle = this.params.style.color;
			//ctx.strokeRect(dimensions.x, dimensions.y, dimensions.width, dimensions.height);
		}
	}

	get version() {
		return Number(this.getInput('Version'));
	}
}
